/**
 * @fileoverview Vertical position strategy for default theme layout.
 * Places draggables on left side and targets on right side in vertical columns.
 */

import { MatchingPair } from '../../utilities/GameUtils.js';
import { PositionStrategy } from './PositionStrategy.js';
import { ShapePositions } from "../../utilities/GameUtils.js";

/**
 * Horizontal positioning strategy (default).
 * Draggables on top, targets on bottom, arranged horizontally.
 */
export class HorizontalPositionStrategy extends PositionStrategy {
    /**
     * Calculate horizontal positions for shapes.
     * @param width - Canvas width
     * @param height - Canvas height
     * @param numPairs - Number of shape pairs
     * @param shapeSize - Size of the shapes (for bounds checking)
     * @returns Positions with DS on left, TS on right
     */
    calculatePositions(width: number, height: number, matchingPairs: MatchingPair[], shapeSize: number, isSequential: boolean): ShapePositions {
        const ds: { x: number; y: number }[] = [];
        const ts: { x: number; y: number }[] = [];
        
        const numPairs = matchingPairs.length;
        // Calculate horizontal spacing
        const availableWidth = width * 0.95;
        const startX = Math.max(availableWidth * 0.05, shapeSize); // Start with some margin
        
        const spacing = availableWidth / (numPairs);
        
        // Position DS on top, TS on bottom
        // Account for shape size to ensure shapes stay within canvas bounds
        const margin = 10; // Minimum margin from canvas edges
        const halfSize = shapeSize / 2;
        
        // DS: Top row - ensure top edge (center - halfSize) is at least 'margin' pixels from top
        const dsY = Math.max(margin + halfSize, height * 0.25);
        
        // TS: Bottom row - ensure bottom edge (center + halfSize) is at least 'margin' pixels from bottom
        const tsY = Math.min(height - margin - halfSize, height * 0.75);
        
        for (let i = 0; i < numPairs; i++) {
            const x = startX + spacing * (i + 0);
            
            ds.push({ x: x, y: dsY });
            if (!isSequential)
                ts.push({ x: x, y: tsY });
        }
        if( isSequential ) {
            // All TS share the SAME position - centered on bottom side
            // Only one will be visible at a time (controlled by game logic)
            const tsCenterX = width / 2;
            const tsCenterY = Math.min(height - margin - halfSize, height * 0.75);
            
            for (let i = 0; i < numPairs; i++) {
                ts.push({ x: tsCenterX, y: tsCenterY });
            }
        }
        
        return { DS_POSITIONS: ds, TS_POSITIONS: ts };
    }

    // shape size should look at the width of the canvas for horizontal strategy to ensure shapes fit within bounds
    getShapeSize(_height: number, width: number, numPairs: number): number {
        let ss = Math.min(140, Math.floor(width / (numPairs * 1.5))); // Base size on available width divided by number of pairs with some spacing
        console.log(`Calculated shape size: ${ss} for width: ${width} and numPairs: ${numPairs}`);
        return Math.max(30, ss );
    }

}

